// client/src/reservations/utils/msClient.js
import { msOpenDates } from './hours';
import { isPastCutoffHHMM } from './timeGate';
import { windowsForDate } from './availabilityLocal';

const toMin = (s) => (+s.slice(0,2))*60 + (+s.slice(3,5));
const toHH  = (m) => String(Math.floor(m/60)).padStart(2,'0')+':'+String(m%60).padStart(2,'0');

function pickGroupRule(rules, seatingConfigId, party) {
  const rows = (rules || []).filter(r => String(r.seating_config_id) === String(seatingConfigId));
  if (!rows.length) return null;
  const exact = rows.find(r => String(r.size_label) === String(party));
  if (exact) return exact;
  const plus = rows.find(r => String(r.size_label).endsWith('+'));
  return exact || plus || rows[0];
}

function maxGroupsForParty(capacityRows, seatingConfigId, party) {
  const rows = (capacityRows || []).filter(r => String(r.seating_config_id) === String(seatingConfigId))
                                   .sort((a,b) => a.total_size - b.total_size);
  if (!rows.length) return { limit: 1, derived: true }; // conservative fallback
  const row = rows.find(r => Number(party) <= Number(r.total_size)) || rows[rows.length-1];
  return { limit: Number(row.max_groups || 1), derived: false };
}

function areaTables(msRules, seatingConfigId, areaType) {
  // If areaType is not provided, return all tables for the seating config (treat as wildcard)
  const all = (msRules?.tables || []).filter(t => String(t.seating_config_id) === String(seatingConfigId));
  if (!areaType) return all;
  return all.filter(t => String(t.area_type) === String(areaType));
}

function areaBufferMin(msRules, seatingConfigId, areaTbls) {
  const sc = (msRules?.seating_configs || []).find(x => String(x.seating_config_id) === String(seatingConfigId));
  const cfgWait = Number(sc?.max_wait_min || 0);
  const turn = Math.max(0, ...areaTbls.map(t => Number(t.turnover_min || 0)));
  return Math.max(cfgWait, turn);
}

function buildComboSet(msRules, seatingConfigId) {
  const set = new Set();
  (msRules?.combinations || [])
    .filter(c => String(c.seating_config_id) === String(seatingConfigId))
    .forEach(c => {
      set.add(`${c.table_id_a}|${c.table_id_b}`);
      set.add(`${c.table_id_b}|${c.table_id_a}`);
    });
  return set;
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && aEnd > bStart; // '[)' intervals on HH:MM
}

// Greedy allocator on top of inventory for a single party
function allocateParty(inv, tables, comboSet, party) {
  // tables: [{id,seats_per_table,combinable,quantity}] but we pass a fresh inv map for counts
  const bySeats = [...tables].sort((a,b)=>a.seats_per_table-b.seats_per_table);
  // 1) single-table exact/sufficient
  let bestIdx = -1, bestSeats = Infinity;
  bySeats.forEach((t, idx) => {
    if (inv.get(t.id) > 0 && t.seats_per_table >= party && t.seats_per_table < bestSeats) {
      bestSeats = t.seats_per_table; bestIdx = idx;
    }
  });
  if (bestIdx >= 0) { inv.set(bySeats[bestIdx].id, inv.get(bySeats[bestIdx].id) - 1); return true; }

  // 2) combinable same-type multi-table: use largest combinable table greedily
  const comb = bySeats.filter(t => t.combinable && inv.get(t.id) > 0).sort((a,b)=>b.seats_per_table-a.seats_per_table);
  let remaining = party;
  const take = new Map();
  for (const t of comb) {
    if (remaining <= 0) break;
    const can = Math.min(inv.get(t.id), Math.ceil(remaining / t.seats_per_table));
    if (can <= 0) continue;
    take.set(t.id, can);
    remaining -= can * t.seats_per_table;
  }
  if (remaining <= 0) {
    // commit
    for (const [id, cnt] of take) inv.set(id, inv.get(id) - cnt);
    return true;
  }

  // 3) cross-type pair (limited to 2 tables to keep it fast)
  let bestPair = null, bestSum = Infinity;
  for (let i=0;i<bySeats.length;i++) {
    for (let j=i;j<bySeats.length;j++) {
      const A = bySeats[i], B = bySeats[j];
      if (!A.combinable || !B.combinable) continue;
      const allowed = (A.id===B.id) || comboSet.has(`${A.id}|${B.id}`);
      if (!allowed) continue;
      const needA = 1, needB = (A.id===B.id? 2:1);
      const have = inv.get(A.id) >= 1 && inv.get(B.id) >= (A.id===B.id ? 2 : 1);
      if (!have) continue;
      const sum = A.seats_per_table + B.seats_per_table;
      if (sum >= party && sum < bestSum) { bestSum = sum; bestPair = [A,B]; }
    }
  }
  if (bestPair) {
    const [A,B] = bestPair;
    inv.set(A.id, inv.get(A.id)-1);
    inv.set(B.id, inv.get(B.id)-1);
    return true;
  }

  return false; // couldn’t place with available inventory/combos
}

export function msSlotsClient({
  dateISO,
  party,
  seatingConfigId,
  areaType,
  hoursCtx,
  msRules,
  live,              // seeded + live
  firstBookableISO,
  tz
}) {
  const windows = windowsForDate(hoursCtx, 'modular_seating', seatingConfigId, dateISO);
  if (!windows?.length) return [];
  // duration from group rules
  const grp = pickGroupRule(msRules?.group_durations, seatingConfigId, party);
  if (!grp) return [];
  const dur = Math.max(grp.min_time_min, Math.min(grp.avg_time_min, grp.max_time_min));

  let tables = areaTables(msRules, seatingConfigId, areaType).map(t => ({
    id: t.id,
    seats_per_table: Number(t.seats_per_table || 0),
    quantity: Number(t.quantity || 0),
    combinable: !!t.combinable,
    turnover_min: Number(t.turnover_min || 0),
  }));
  if (!tables.length) {
    // if no tables matched the requested areaType but there are tables for the seating config in other areas,
    // fall back to using all tables for that seating config (helps when callers don't supply areaType)
    const alt = (msRules?.tables || []).filter(t => String(t.seating_config_id) === String(seatingConfigId));
    if (alt.length) {
      try { console.warn(`[msSlotsClient] no tables for areaType='${areaType}' seatingConfigId=${seatingConfigId}; falling back to ${alt.length} tables across all areas`); } catch(e){}
      tables = alt.map(t => ({
        id: t.id,
        seats_per_table: Number(t.seats_per_table || 0),
        quantity: Number(t.quantity || 0),
        combinable: !!t.combinable,
        turnover_min: Number(t.turnover_min || 0),
      }));
    } else {
      return []; // nothing to seat on in any area for this seating config
    }
  }

  const comboSet = buildComboSet(msRules, seatingConfigId);
  const bufferMin = areaBufferMin(msRules, seatingConfigId, tables);

  // capacity band (with sensible fallback)
  const cap = maxGroupsForParty(msRules?.capacity, seatingConfigId, party);

  // collect overlapping blocks for this area/date once
  const dayBlocks = (live?.['ms_table_type'] || []).filter(b =>
    String(b.ms_seating_config_id) === String(seatingConfigId) &&
    String(b.ms_area_type) === String(areaType) &&
    b.slot_start?.slice(0,10) === dateISO
  ).map(b => ({
    start: b.slot_start.slice(11,16),
    end:   b.slot_end.slice(11,16),
    party: Number(b.party_size || b.guests_json?.party_size || 1)
  }));

  // generate candidate slots
  const candidates = [];
  for (const w of windows) {
    const a = toMin(w.start), b = toMin(w.end);
    for (let t = a; t + dur <= b; t += 15) {
      const start = toHH(t), end = toHH(t + dur);
      if (isPastCutoffHHMM({ dayISO: dateISO, hhmm: start, firstBookableISO, tz })) continue;

      // Which blocks overlap this slot (with buffer)
      const slotStart = toMin(start) - bufferMin;
      const slotEnd   = toMin(end)   + bufferMin;
      const overlapsNow = dayBlocks.filter(x => overlaps(slotStart, slotEnd, toMin(x.start), toMin(x.end)));

      // capacity-band gate (count only bookings that fall in the same band as this party)
      const sameBandCount = overlapsNow.filter(x => {
        const band = maxGroupsForParty(msRules?.capacity, seatingConfigId, x.party);
        return band.limit === cap.limit || cap.derived; // if derived=1, we just fall back to total inventory
      }).length;
      if (!cap.derived && sameBandCount + 1 > cap.limit) continue;

      // inventory gate – allocate overlaps + this party
      const inv = new Map(tables.map(t => [t.id, t.quantity]));
      let ok = true;

      // Place existing overlaps (largest first)
      const sorted = overlapsNow.sort((a,b) => b.party - a.party);
      for (const g of sorted) {
        if (!allocateParty(inv, tables, comboSet, g.party)) { ok = false; break; }
      }
      if (!ok) continue;

      // Try to place *this* party
      if (!allocateParty(inv, tables, comboSet, Number(party || 1))) continue;

      candidates.push({ start, end });
      if (candidates.length >= 60) break;
    }
    if (candidates.length >= 60) break;
  }
  return candidates;
}
